import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { Box, Button, Chip, IconButton, SxProps, Theme } from '@mui/material';
import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CLEARED_MARKETPLACE_FILTERS,
  CLEAR_FILTERS_LABEL,
  FILTER_CHIP_HEIGHT,
  getClearSearchAriaLabel,
  getQueryChipLabel,
  getRemoveFilterAriaLabel,
  MARKETPLACE_FILTER_KIND_LABELS,
} from '../../../constants/marketplace';
import { HIDDEN_SCROLLBAR_SX, HOVER_MEDIA, PILL_BORDER_RADIUS } from '../../../constants/ui';
import { useHorizontalWheelScroll } from '../../../hooks/useHorizontalWheelScroll';
import { marketplaceSetFiltersAction, marketplaceSetSupplierFilterAction } from '../../../store/actions/marketplace';
import {
  selectHasActiveMarketplaceFilters,
  selectMarketplaceFilters,
  selectSelectedMarketplaceFilters,
} from '../../../store/selectors/marketplace';
import { IMarketplaceSelectedFilter, MarketplaceFilterKindEnum } from '../../../types/marketplace';
import { MarketplaceFilterMenus } from './MarketplaceFilterMenu';
import { SupplierFilterChip } from './SupplierFilterChip';

interface IProps {
  // Desktop seats the menus beside the search and keeps this row for chips only.
  withMenus?: boolean;
}

const railSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 1,
  minHeight: FILTER_CHIP_HEIGHT,
  overflowX: 'auto',
  py: 0.25,
  '& > *': { flexShrink: 0 },
  ...HIDDEN_SCROLLBAR_SX,
};

const chipSx: SxProps<Theme> = {
  height: FILTER_CHIP_HEIGHT,
  borderRadius: PILL_BORDER_RADIUS,
  fontWeight: 400,
  backgroundColor: 'background.paper',
  '& .MuiChip-deleteIcon': { fontSize: 18 },
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: 'action.hover' },
  },
};

const chipDividerSx: SxProps<Theme> = { width: '1px', height: 24, bgcolor: 'divider' };

const clearLeadSx: SxProps<Theme> = {
  width: FILTER_CHIP_HEIGHT,
  height: FILTER_CHIP_HEIGHT,
  border: 1,
  borderColor: 'grey.400',
  backgroundColor: 'background.paper',
  color: 'action.active',
};

const clearButtonSx: SxProps<Theme> = {
  height: FILTER_CHIP_HEIGHT,
  px: 1,
  typography: 'body1',
  fontWeight: 400,
  textTransform: 'none',
  whiteSpace: 'nowrap',
};

interface ISelectedFilterChipProps {
  filter: IMarketplaceSelectedFilter;
  onRemove: (kind: MarketplaceFilterKindEnum, value: string) => void;
}

const SelectedFilterChip = ({ filter, onRemove }: ISelectedFilterChipProps) => {
  const handleRemove = useCallback(() => onRemove(filter.kind, filter.value), [onRemove, filter.kind, filter.value]);

  const ariaLabel = useMemo(
    () => getRemoveFilterAriaLabel(MARKETPLACE_FILTER_KIND_LABELS[filter.kind], filter.label),
    [filter.kind, filter.label],
  );

  return (
    <Chip
      label={filter.label}
      variant="outlined"
      onClick={handleRemove}
      onDelete={handleRemove}
      aria-label={ariaLabel}
      data-action={`${MARKETPLACE_FILTER_KIND_LABELS[filter.kind]}_filter_chip`}
      sx={chipSx}
    />
  );
};

export const MarketplaceFilters = ({ withMenus = true }: IProps) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectMarketplaceFilters);
  const selectedFilters = useSelector(selectSelectedMarketplaceFilters);
  const hasActiveFilters = useSelector(selectHasActiveMarketplaceFilters);
  const railRef = useRef<HTMLDivElement>(null);

  useHorizontalWheelScroll(railRef);

  const query = filters.query.trim();

  const handleClearAll = useCallback(() => {
    dispatch(marketplaceSetFiltersAction(CLEARED_MARKETPLACE_FILTERS));
    dispatch(marketplaceSetSupplierFilterAction(false));
  }, [dispatch]);

  const handleClearQuery = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ query: '' }));
  }, [dispatch]);

  const handleRemoveFilter = useCallback(
    (kind: MarketplaceFilterKindEnum, value: string) => {
      dispatch(marketplaceSetFiltersAction({ [kind]: filters[kind].filter((current) => current !== value) }));
    },
    [dispatch, filters],
  );

  const queryChipLabel = useMemo(() => getQueryChipLabel(query), [query]);
  const clearSearchAriaLabel = useMemo(() => getClearSearchAriaLabel(query), [query]);

  const hasChips = !!query || selectedFilters.length > 0;
  const isLeadClearShown = withMenus && hasActiveFilters;
  const isChipDividerShown = withMenus && hasChips;
  const isTrailClearShown = !withMenus && hasActiveFilters;

  return (
    <Box ref={railRef} sx={railSx}>
      {isLeadClearShown && (
        <IconButton
          onClick={handleClearAll}
          aria-label={CLEAR_FILTERS_LABEL}
          title={CLEAR_FILTERS_LABEL}
          data-action="clear_filters_button"
          sx={clearLeadSx}
        >
          <FilterAltOffOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      {withMenus && <SupplierFilterChip />}
      {withMenus && <MarketplaceFilterMenus />}
      {isChipDividerShown && <Box aria-hidden sx={chipDividerSx} />}
      {!!query && (
        <Chip
          label={queryChipLabel}
          variant="outlined"
          onClick={handleClearQuery}
          onDelete={handleClearQuery}
          aria-label={clearSearchAriaLabel}
          data-action="search_filter_chip"
          sx={chipSx}
        />
      )}
      {selectedFilters.map((filter) => (
        <SelectedFilterChip key={`${filter.kind}:${filter.value}`} filter={filter} onRemove={handleRemoveFilter} />
      ))}
      {isTrailClearShown && (
        <Button
          variant="text"
          color="primary"
          onClick={handleClearAll}
          data-action="clear_filters_button"
          sx={clearButtonSx}
        >
          {CLEAR_FILTERS_LABEL}
        </Button>
      )}
    </Box>
  );
};
